/**
 * BrandContext
 * ============
 * Provides the authenticated brand (NAWABI | BAWARCHI | PARADISE) throughout
 * the customer workflow.
 *
 * The brand is derived exclusively from the server-issued JWT stored in
 * sessionStorage by BasicAuth.js after a successful gateway login.
 * Clients cannot override the brand.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Session key written by BasicAuth.js
const SESSION_KEY_BRAND = 'gatewayBrand';

// Brand display meta
export const BRAND_META = {
  NAWABI: {
    name:        'Nawabi',
    fullName:    'Nawabi Hyderabad House',
    tagline:     'Authentic Hyderabadi Cuisine',
    emoji:       '🍛',
    color:       '#c8860a',
  },
  BAWARCHI: {
    name:        'Bawarchi',
    fullName:    'Bawarchi Restaurant',
    tagline:     'Flavors of Hyderabad',
    emoji:       '🫕',
    color:       '#b5451b',
  },
  PARADISE: {
    name:        'Paradise',
    fullName:    'Paradise Biryani',
    tagline:     'The Original Biryani Experience',
    emoji:       '🍲',
    color:       '#1a6b3c',
  },
};

const BrandContext = createContext(null);

export const useBrand = () => {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
};

export const BrandProvider = ({ children }) => {
  const [brand, setBrand] = useState(() => {
    return sessionStorage.getItem(SESSION_KEY_BRAND) || null;
  });

  // Keep in sync if another tab / BasicAuth updates the value
  useEffect(() => {
    const onStorage = () => {
      const b = sessionStorage.getItem(SESSION_KEY_BRAND);
      setBrand(b || null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const clearBrand = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY_BRAND);
    setBrand(null);
  }, []);

  const meta = (brand && BRAND_META[brand]) || null;

  return (
    <BrandContext.Provider value={{ brand, meta, clearBrand }}>
      {children}
    </BrandContext.Provider>
  );
};

export default BrandContext;
